import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setMemberships(data.memberships || []);
      setFeatures(data.features || {});
      return data;
    } catch (e) {
      setUser(null);
      setMemberships([]);
      return null;
    }
  }, []);

  useEffect(() => { refreshMe().finally(() => setLoading(false)); }, [refreshMe]);

  // Server is authoritative: any 401 (session expired, idle timeout, revoked) clears local auth
  // state so protected routes redirect to /login on their next render - no client-side guessing.
  useEffect(() => {
    const id = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          setUser(null);
          setMemberships([]);
        }
        // Connect rollout switch: the server refuses client-workspace calls while it is off; an existing session learns this on its next call.
        if (error?.response?.status === 403 && String(error.response.headers?.["x-connect-disabled"] || "") === "true") {
          setFeatures((f) => ({ ...f, connect_client_workspace: false }));
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  const requestOtp = async (email) => {
    const { data } = await api.post("/auth/otp/request", { email });
    if (!data || typeof data !== "object" || typeof data.message !== "string") {
      throw new Error("Unexpected response from the sign-in service");
    }
    return data;
  };

  const verifyOtp = async (email, code) => {
    const { data } = await api.post("/auth/otp/verify", { email, code });
    await refreshMe();
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(null);
      setMemberships([]);
    }
  };

  const orgIdsForType = (type) => memberships.filter((m) => m.org_type === type).map((m) => m.organisation_id);
  const hasWorkspace = (type) => memberships.some((m) => m.org_type === type);
  const primaryWorkspaceType = () => {
    const primary = memberships.find((m) => m.is_primary) || memberships[0];
    return primary ? primary.org_type : null;
  };

  const connectEnabled = features.connect_client_workspace === true;

  const value = {
    user, memberships, features, connectEnabled, loading, refreshMe, requestOtp, verifyOtp, logout,
    orgIdsForType, hasWorkspace, primaryWorkspaceType,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
